/**
 * "Cross-sections (DXF)" — the dock's export dialog.
 *
 * Everything happens in the browser: the sections, the alignment and the bins
 * are already here, DXF needs no converter, and there is therefore no job, no
 * batch and no toast to track. The download is synchronous.
 *
 * Three layouts, any combination of which lands in one file (spec §4):
 * sheet (tiled 2D), plan (top-down, printable) and model (georeferenced 3D).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Alignment } from '@/core/alignment';
import { downloadFile } from '@/lib/download';
import { dxfDocument, type DxfLayer } from '@/lib/stationVolumes/dxf';
import { staOnly } from '@/lib/stationVolumes/format';
import {
  MAX_DXF_SECTIONS,
  MODEL_LAYERS,
  PLAN_LAYERS,
  SHEET_LAYERS,
  defaultSheetVe,
  sectionDxfFilename,
  sectionDxfScope,
  sectionModelEntities,
  sectionPlanEntities,
  sectionSeriesLayers,
  sectionSheetEntities,
  type SectionInput,
} from '@/lib/stationVolumes/sectionDxf';
import { offsetAxisOf, VE_PRESETS } from '@/lib/stationVolumes/sectionModel';
import type { StationBin, StationSectionLine, StationVolumesJson } from '@/lib/stationVolumes/types';
import { useDialog } from '@/providers/DialogProvider';

export interface StationSectionDxfDialogProps {
  json: StationVolumesJson;
  /** Section rows in offset-column order, one per drawable station. */
  sections: SectionInput[];
  /** True when `sections` came from the bin-mean planes, not true station cuts. */
  intervalMeans: boolean;
  bins: StationBin[];
  stations: StationSectionLine[] | null;
  /** Absent when the analysed alignment is no longer in the project. */
  alignment: Alignment | undefined;
  selection: { fromBin: number; toBin: number } | null;
  alignmentName: string;
}

type Layout = 'sheet' | 'plan' | 'model';

export const StationSectionDxfDialog = ({
  json,
  sections,
  intervalMeans,
  bins,
  stations,
  alignment,
  selection,
  alignmentName,
}: StationSectionDxfDialogProps) => {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();

  // Plan and model place geometry in project coordinates, so both need the
  // live alignment. Sheet does not — it is a drawing in its own space — which
  // is why a run whose alignment has left the project can still export a sheet.
  const canGeoreference = !!alignment;
  const [layouts, setLayouts] = useState<Record<Layout, boolean>>({
    sheet: true,
    plan: false,
    model: false,
  });
  const [ve, setVe] = useState<number>(() =>
    defaultSheetVe(sections, json.params.swathLeftM + json.params.swathRightM),
  );
  const [columns, setColumns] = useState(4);
  const [error, setError] = useState<string | null>(null);

  // The axis the rows were WRITTEN against, not one synthesised from the
  // swath: the columns are anchored on the centreline, so `-swathLeftM` is
  // half a bin off and the sheet would draw every profile shifted against its
  // own offset ticks. Shared with the chart via `offsetAxisOf`.
  const axis = useMemo(() => offsetAxisOf(json, intervalMeans), [json, intervalMeans]);

  const scope = useMemo(() => sectionDxfScope(sections, selection), [sections, selection]);
  const binScope = useMemo(() => {
    if (!selection) return bins;
    const lo = Math.max(0, Math.min(selection.fromBin, selection.toBin));
    const hi = Math.min(bins.length - 1, Math.max(selection.fromBin, selection.toBin));
    return lo <= hi ? bins.slice(lo, hi + 1) : [];
  }, [bins, selection]);

  const anyLayout = layouts.sheet || (canGeoreference && (layouts.plan || layouts.model));
  // Stated the moment the dialog opens, not held back until the user commits:
  // a blocked export the user only discovers on click is a wasted round trip.
  const blocked = scope.overCap
    ? t('stationVolumes.export.sectionsDxfTooMany', { count: scope.lines.length, max: MAX_DXF_SECTIONS })
    : scope.lines.length === 0
      ? t('stationVolumes.export.sectionsDxfEmpty')
      : null;

  const toggle = (k: Layout) => setLayouts((p) => ({ ...p, [k]: !p[k] }));

  const onExport = () => {
    if (blocked) {
      setError(blocked);
      return;
    }

    const layers: DxfLayer[] = [];
    const entities: string[] = [];
    const seen = new Set<string>();
    const addLayers = (ls: readonly DxfLayer[]) => {
      for (const l of ls) {
        if (seen.has(l.name)) continue;
        seen.add(l.name);
        layers.push(l);
      }
    };

    if (layouts.sheet) {
      addLayers(SHEET_LAYERS);
      // TABLES has to declare every layer ENTITIES draws on, and a stack draws
      // one per extra level. Resolved over the SCOPE, so the layer a level got
      // is the layer the writer below puts it on.
      addLayers(sectionSeriesLayers(scope.lines));
      entities.push(
        ...sectionSheetEntities(scope.lines, {
          ...axis,
          ve,
          columns,
          // A cell is `swath` wide and `zSpan * ve` tall; a fifth of the swath
          // is a gutter that reads as a gutter at any swath width.
          gapX: Math.max(json.params.swathLeftM + json.params.swathRightM, 10) * 0.2,
          gapY: Math.max(json.params.swathLeftM + json.params.swathRightM, 10) * 0.25,
          // 2.2% of the swath, not 3%: at 3% the title block ran better than
          // half the width of its own cell, which is what made a 4-column sheet
          // read as crowded even where nothing actually overlapped.
          textHeight: Math.max(json.params.swathLeftM + json.params.swathRightM, 10) * 0.022,
          note: intervalMeans ? t('stationVolumes.export.sectionsDxfIntervalMeans') : undefined,
        }),
      );
    }
    if (layouts.plan && alignment) {
      addLayers(PLAN_LAYERS);
      entities.push(
        ...sectionPlanEntities(binScope, stations, alignment, {
          swathLeftM: json.params.swathLeftM,
          swathRightM: json.params.swathRightM,
          textHeight: Math.max(json.params.stationIntervalM * 0.15, 1),
          // Roughly 40 labels across the drawing, whatever the interval.
          labelEvery: Math.max(1, Math.ceil(scope.lines.length / 40)),
        }),
      );
    }
    if (layouts.model && alignment) {
      addLayers(MODEL_LAYERS);
      addLayers(sectionSeriesLayers(scope.lines));
      entities.push(
        ...sectionModelEntities(scope.lines, alignment, {
          ...axis,
          // Metres of model space, so it has to scale with the corridor, not
          // with the page. Same rule as the plan layout's chainage labels: at
          // a 20 m station interval a 3 m label reads in a 3D view and still
          // clears its neighbours.
          textHeight: Math.max(json.params.stationIntervalM * 0.15, 1),
        }),
      );
    }

    const from = scope.lines[0].staDisplayM;
    const to = scope.lines[scope.lines.length - 1].staDisplayM;
    downloadFile(dxfDocument(layers, entities), sectionDxfFilename(alignmentName, from, to), 'application/dxf');
    dismissActiveDialog();
  };

  const rangeLabel =
    scope.lines.length > 0
      ? `${staOnly(scope.lines[0].staDisplayM)} – ${staOnly(scope.lines[scope.lines.length - 1].staDisplayM)}`
      : '—';

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('stationVolumes.export.sectionsDxfTitle')}</DialogTitle>
        <DialogDescription>
          {t('stationVolumes.export.sectionsDxfRange', { count: scope.lines.length, range: rangeLabel })}
        </DialogDescription>
      </DialogHeader>

      <div className='flex flex-col gap-4 py-2'>
        <div className='flex flex-col gap-2'>
          <Label>{t('stationVolumes.export.sectionsDxfLayout')}</Label>
          <label className='flex items-start gap-2 text-sm'>
            <Checkbox
              data-testid='sv-dxf-layout-sheet'
              checked={layouts.sheet}
              onCheckedChange={() => toggle('sheet')}
            />
            <span>
              {t('stationVolumes.export.sectionsDxfSheet')}
              <span className='block text-xs text-muted-foreground'>
                {t('stationVolumes.export.sectionsDxfSheetHelp')}
              </span>
            </span>
          </label>
          <label className='flex items-start gap-2 text-sm'>
            <Checkbox
              data-testid='sv-dxf-layout-plan'
              checked={layouts.plan && canGeoreference}
              disabled={!canGeoreference}
              onCheckedChange={() => toggle('plan')}
            />
            <span>
              {t('stationVolumes.export.sectionsDxfPlan')}
              <span className='block text-xs text-muted-foreground'>
                {t('stationVolumes.export.sectionsDxfPlanHelp')}
              </span>
            </span>
          </label>
          <label className='flex items-start gap-2 text-sm'>
            <Checkbox
              data-testid='sv-dxf-layout-model'
              checked={layouts.model && canGeoreference}
              disabled={!canGeoreference}
              onCheckedChange={() => toggle('model')}
            />
            <span>
              {t('stationVolumes.export.sectionsDxfModel')}
              <span className='block text-xs text-muted-foreground'>
                {t('stationVolumes.export.sectionsDxfModelHelp')}
              </span>
            </span>
          </label>
          {!canGeoreference && (
            <p data-testid='sv-dxf-no-alignment' className='text-xs text-amber-600 dark:text-amber-500'>
              {t('stationVolumes.export.sectionsDxfNoAlignment')}
            </p>
          )}
        </div>

        {layouts.sheet && (
          <div className='grid grid-cols-2 gap-3'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='sv-dxf-ve'>{t('stationVolumes.section.veGroup')}</Label>
              <Select value={String(ve)} onValueChange={(v) => setVe(Number(v))}>
                <SelectTrigger id='sv-dxf-ve' data-testid='sv-dxf-ve'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VE_PRESETS.map((p) => (
                    <SelectItem key={p} value={String(p)}>
                      {t('stationVolumes.section.vePreset', { factor: p })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='sv-dxf-columns'>{t('stationVolumes.export.sectionsDxfColumns')}</Label>
              <Select value={String(columns)} onValueChange={(v) => setColumns(Number(v))}>
                <SelectTrigger id='sv-dxf-columns' data-testid='sv-dxf-columns'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 6, 8].map((c) => (
                    <SelectItem key={c} value={String(c)}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {intervalMeans && (
          <p data-testid='sv-dxf-interval-means' className='text-xs text-muted-foreground'>
            {t('stationVolumes.export.sectionsDxfIntervalMeans')}
          </p>
        )}
        {(blocked || error) && (
          <p data-testid='sv-dxf-error' className='text-xs text-destructive'>
            {blocked ?? error}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type='button' variant='outline' onClick={dismissActiveDialog}>
          {t('stationVolumes.export.sectionsDxfCancel')}
        </Button>
        <Button
          type='button'
          data-testid='sv-dxf-confirm'
          disabled={!anyLayout || !!blocked}
          onClick={onExport}
        >
          {t('stationVolumes.export.sectionsDxfConfirm')}
        </Button>
      </DialogFooter>
    </>
  );
};
